import React, { useState, useRef, useEffect } from 'react';
import { Box, Button } from '@mui/material';

interface ContentPreviewProps {
  maxHeight?: number;
  gradientHeight?: number;
  gradientColor?: string;
  expandText?: string;
  collapseText?: string;
  showControls?: boolean;
}

export const ContentPreview = ({
  children,
  maxHeight = 160,
  gradientHeight = 60,
  gradientColor = 'rgba(239, 241, 242, 0.95)',
  expandText = 'Show more',
  collapseText = 'Show less',
  showControls = true,
}: ContentPreviewProps & React.PropsWithChildren) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState<boolean | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) {
      const contentHeight = contentRef.current.scrollHeight;
      setNeedsExpansion(contentHeight > maxHeight);
    }
  }, [children, maxHeight]);

  if (!showControls) {
    return <>{children}</>;
  }

  // Still measuring - render without constraints to get true height
  if (needsExpansion === null) {
    return (
      <Box ref={contentRef} sx={{ overflow: 'visible' }}>
        {children}
      </Box>
    );
  }

  // Content fits within maxHeight - no need for expansion
  if (!needsExpansion) {
    return <>{children}</>;
  }

  // Content exceeds maxHeight - show with expansion controls
  return (
    <>
      <Box
        sx={{
          maxHeight: !isExpanded ? `${maxHeight}px` : 'none',
          overflow: !isExpanded ? 'hidden' : 'visible',
          position: 'relative',
          transition: 'max-height 0.3s ease',
        }}
      >
        {children}

        {!isExpanded && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: `${gradientHeight}px`,
              background: `linear-gradient(to bottom, transparent, ${gradientColor})`,
              pointerEvents: 'none',
            }}
          />
        )}
      </Box>

      <Button
        variant="text"
        size="small"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ mt: 1 }}
      >
        {isExpanded ? collapseText : expandText}
      </Button>
    </>
  );
};
